import { validEmail } from "@origin-digital/validation";

export interface HolderDetails {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  mobileNumber: string;
  email: string;
}
export const EMPTY_DETAILS: HolderDetails = {
  firstName: "",
  lastName: "",
  dateOfBirth: "",
  mobileNumber: "",
  email: "",
};
export const fields: {
  key: keyof HolderDetails;
  label: string;
  maxLength: number;
}[] = [
  { key: "firstName", label: "First name", maxLength: 100 },
  { key: "lastName", label: "Last name", maxLength: 100 },
  { key: "dateOfBirth", label: "Date of birth", maxLength: 10 },
  { key: "mobileNumber", label: "Mobile number", maxLength: 20 },
  { key: "email", label: "Email", maxLength: 254 },
];
export const normaliseDetails = (details: HolderDetails): HolderDetails => ({
  firstName: details.firstName.trim(),
  lastName: details.lastName.trim(),
  dateOfBirth: details.dateOfBirth.trim(),
  email: details.email.trim(),
  mobileNumber: details.mobileNumber
    .trim()
    .replace(/ /g, "")
    .replace(/^\+61/, "0"),
});
export const validateDetails = (details: HolderDetails, now = new Date()) => {
  const errors: Partial<Record<keyof HolderDetails, string>> = {};
  const value = normaliseDetails(details);
  fields.forEach(({ key, label, maxLength }) => {
    if (!value[key]) errors[key] = `Enter ${label.toLowerCase()}.`;
    else if (
      Array.from(details[key]).some(
        (character) =>
          character.charCodeAt(0) < 32 ||
          (character.charCodeAt(0) >= 127 && character.charCodeAt(0) <= 159) ||
          character === "\u2028" ||
          character === "\u2029"
      ) ||
      value[key].length > maxLength
    )
      errors[key] = `Enter a valid ${label.toLowerCase()}.`;
  });
  (["firstName", "lastName"] as const).forEach((key) => {
    if (
      value[key] &&
      (!/^[\p{L}\p{M} '\u2019-]+$/u.test(value[key]) ||
        !/\p{L}/u.test(value[key]))
    )
      errors[key] = "Use letters, spaces, apostrophes or hyphens.";
  });
  if (value.mobileNumber && !/^04\d{8}$/.test(value.mobileNumber))
    errors.mobileNumber = "Enter a valid Australian mobile number.";
  if (value.email && validEmail({ error: "invalid" })(value.email))
    errors.email = "Enter a valid email address.";
  if (value.dateOfBirth && !errors.dateOfBirth) {
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value.dateOfBirth);
    const [, day, month, year] = match || [];
    const date = new Date(0);
    date.setUTCFullYear(Number(year), Number(month) - 1, Number(day));
    if (
      !match ||
      Number(year) < 1 ||
      date.getUTCFullYear() !== Number(year) ||
      date.getUTCMonth() !== Number(month) - 1 ||
      date.getUTCDate() !== Number(day)
    )
      errors.dateOfBirth = "Enter a real date in dd/mm/yyyy format.";
    else if (Number(year) < 1900)
      errors.dateOfBirth = "Enter a date on or after 01/01/1900.";
    else {
      const parts = new Intl.DateTimeFormat("en-AU", {
        timeZone: "Australia/Sydney",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).formatToParts(now);
      const get = (type: string) =>
        Number(parts.find((part) => part.type === type)!.value);
      const birthday = Number(year) + 16;
      if (
        birthday > get("year") ||
        (birthday === get("year") &&
          Number(month) * 100 + Number(day) > get("month") * 100 + get("day"))
      )
        errors.dateOfBirth =
          "The joint account holder must be at least 16 years old.";
    }
  }
  return errors;
};

/** Latest eligible DOB as local calendar components derived from Sydney today. */
export const latestEligibleDateOfBirth = (now = new Date()): Date => {
  const parts = new Intl.DateTimeFormat("en-AU", {
    timeZone: "Australia/Sydney",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const get = (type: string) =>
    Number(parts.find((part) => part.type === type)!.value);
  const date = new Date(0);
  date.setHours(0, 0, 0, 0);
  date.setFullYear(get("year") - 16, get("month"), 0);
  date.setDate(Math.min(get("day"), date.getDate()));
  return date;
};
